import { calculatePolygonCentroid } from 'components/BaseMap/util/BaseMap.utils';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetFemaAlertContractQuery,
  useGetFemaJurisdictionsQuery,
  useGetFemaMetaQuery,
} from 'services/ipaws/ipaws.api';
import useIpawsSubmissionFlow from '../../hooks/useIpawsSubmissionFlow';
import { scopeOptions, statusOptions } from './config/Ipaws.config';
import {
  selectFemaUpdateContext,
  selectIpawsAlertDraft,
} from '../../state/selectors/alertCreatorSelectors';
import {
  applyIpawsResolvedJurisdiction,
  clearFemaUpdateContext,
  clearIpawsResolvedJurisdiction,
  setMapCenter,
  updateActiveAlertDraft,
} from '../../state/slices/alertCreatorSlice';
import {
  createInitialIpawsAlertDraft,
  formatIpawsDateTimeWithOffset,
  getIpawsDraftFindings,
  getUpdateAreaShapes,
  getUpdateDeliveryChannels,
} from '../../utils/alertCreator.utils';
import {
  acceptFemaJurisdictionsForClass,
  getChosenFemaEnvironment,
  getFemaEnvironmentFinding,
  getWireFemaEnvironment,
  greyOutIpawsOptions,
  isIpawsScopeAllowedForClass,
  isIpawsStatusAllowedForClass,
  PRODUCTION_SCOPE,
  PRODUCTION_STATUS,
  resolveFemaEnvironmentClass,
} from '../../utils/femaEnvironment.utils';
import {
  permittedAreaForDraft,
  resolvedFromAuthorized,
  unauthorizedGeocodeFinding,
} from '../../utils/femaJurisdictions.utils';
import { describeFemaDestination } from '../../utils/femaMeta.utils';
import { getFemaSubmissionContractOptions } from '../../utils/femaSubmissionContract.utils';
import {
  IPAWS_EAS_AUDIO_RESOURCE_DESCRIPTION,
  IPAWS_EAS_AUDIO_RESOURCE_MIME_TYPE,
} from '../../utils/ipawsAudio.utils';
import { readIpawsChannelRules } from '../../utils/ipawsChannelRules.utils';
import IpawsAuthoringSections from './components/IpawsAuthoringSections/IpawsAuthoringSections';
import IpawsSubmissionPreviewModal from './components/IpawsSubmissionPreviewModal/IpawsSubmissionPreviewModal';
import IpawsSubmissionResultModal from './components/IpawsSubmissionResultModal/IpawsSubmissionResultModal';
import { FEMA_JURISDICTIONS_CLASS_MISMATCH } from './config/femaEnvironment.config';

/**
 * The IPAWS development tools — the "Fill test message" button and the Test Matrix — exist only
 * in an opted-in dev build (Mike, 2026-09-17: "should not show up at all and should be compiled
 * out"). Both operands are build-time literals (`rsbuild.config.ts`, `Docs/ENV_AND_BUILDS.md`),
 * so the bundler settles this `if` while parsing: when it is false the `require` is never
 * collected, and the tools, the test fill, the matrix and its runner are absent from the output
 * rather than hidden. The same idiom loads `useRenderTracker` in `App.jsx`. Checked on every
 * call, not once at load, so a test can set the flags per render.
 */
let ipawsDevToolsComponent = null;
const getIpawsDevTools = () => {
  if (process.env.NODE_ENV === 'development' && process.env.PUBLIC_DEV_COMMAND === 'true') {
    if (!ipawsDevToolsComponent) {
      ipawsDevToolsComponent = require('./components/IpawsDevTools/IpawsDevTools').default;
    }
    return ipawsDevToolsComponent;
  }
  return null;
};

/**
 * Shows the IPAWS form, checks the draft, and manages the preview and send steps.
 * IPAWS uses its own flow so its rules and data do not get mixed with RescAlert alerts.
 */
const IpawsAlertFields = () => {
  const ipawsDraft = useSelector(selectIpawsAlertDraft);
  const femaUpdateContext = useSelector(selectFemaUpdateContext);
  const dispatch = useDispatch();
  const femaAlertContract = useGetFemaAlertContractQuery();
  const contractOptions = useMemo(
    () => getFemaSubmissionContractOptions(femaAlertContract.data),
    [femaAlertContract.data],
  );

  useEffect(() => {
    const source = femaUpdateContext;
    const info = source?.payload?.info?.[0];
    if (!source?.payload || !info) return;
    const areaShapes = getUpdateAreaShapes(info.area);

    dispatch(
      updateActiveAlertDraft({
        messageType: 'Update',
        status: source.payload.status ?? 'Test',
        scope: source.payload.scope ?? 'Public',
        restriction: source.payload.restriction ?? '',
        addresses: (source.payload.addresses ?? []).join(' '),
        references: [
          ...(source.payload.references ?? []),
          {
            sender: source.envelope.sender,
            identifier: source.payload.identifier,
            sent: source.envelope.sent,
          },
        ],
        headline: info.headline ?? '',
        description: info.description ?? '',
        effective: formatIpawsDateTimeWithOffset(info.effective ?? source.envelope.sent),
        expires: info.expires ? formatIpawsDateTimeWithOffset(info.expires) : '',
        event: info.event ?? '',
        eventCode: info.eventCode?.find(({ valueName }) => valueName === 'SAME')?.value ?? '',
        category: info.category ?? [],
        responseType: info.responseType ?? [],
        urgency: info.urgency ?? '',
        severity: info.severity ?? '',
        certainty: info.certainty ?? '',
        instruction: info.instruction ?? '',
        web: info.web ?? '',
        contact: info.contact ?? '',
        language: info.language ?? 'en-US',
        parameters: info.parameter ?? [],
        deliveryChannels: getUpdateDeliveryChannels(info.parameter ?? []),
        geocodes: info.area?.[0]?.geocode ?? [],
        areaDesc: info.area?.[0]?.areaDesc ?? '',
        areaShapes,
      }),
    );
    const firstShape = areaShapes[0];
    const firstPosition =
      firstShape?.type === 'circle' ? firstShape.center : firstShape?.coordinates?.[0];
    if (firstPosition) dispatch(setMapCenter(firstPosition));
  }, [dispatch, femaUpdateContext]);

  // The areas the COG may alert — the COG Profile FEMA issues, via the service (card BE-13).
  // Multi-COG contract §1: one answer per class. The query asks for the chosen class, and an
  // answer that echoes another class is not used.
  const femaEnvironment = ipawsDraft.femaEnvironment ?? null;
  const femaJurisdictionsQuery = useGetFemaJurisdictionsQuery(
    femaEnvironment ? { environment: femaEnvironment } : undefined,
  );
  const { data: femaJurisdictionsData, mismatch: isJurisdictionsClassMismatch } =
    acceptFemaJurisdictionsForClass(femaJurisdictionsQuery.data, femaEnvironment);
  const femaJurisdictions = {
    data: femaJurisdictionsData,
    isLoading: femaJurisdictionsQuery.isLoading,
    isError: femaJurisdictionsQuery.isError || isJurisdictionsClassMismatch,
    error: isJurisdictionsClassMismatch
      ? { message: FEMA_JURISDICTIONS_CLASS_MISMATCH }
      : femaJurisdictionsQuery.error,
  };
  const authorizedJurisdictions = useMemo(
    () => femaJurisdictionsData?.jurisdictions ?? [],
    [femaJurisdictionsData],
  );
  // The channel rules (card FE-06): the COG grants and EAS-ORG from `/jurisdictions`, the static
  // channel facts and limits from `/meta/type`. Each falls back when the server does not send it.
  const channelRules = useMemo(
    () => readIpawsChannelRules(femaJurisdictionsData, femaAlertContract.data),
    [femaAlertContract.data, femaJurisdictionsData],
  );
  const authorizedAreas = {
    jurisdictions: authorizedJurisdictions,
    cogId: femaJurisdictions.data?.cogId ?? null,
    organizationName: femaJurisdictions.data?.organizationName ?? null,
    tenantNote: femaJurisdictions.data?.tenantNote ?? null,
    isLoading: femaJurisdictions.isLoading,
    isError: femaJurisdictions.isError,
    error: femaJurisdictions.error,
  };

  // An authorized pick is the county target, exactly as a searched county would be, and the
  // map moves to it. The area's boundary is drawn for reference; it is never sent as a shape.
  const selectAuthorizedJurisdiction = (jurisdiction) => {
    const resolved = resolvedFromAuthorized(jurisdiction);
    if (!resolved) return;
    dispatch(
      applyIpawsResolvedJurisdiction({
        jurisdiction: resolved,
        sameGeocode: jurisdiction.sameCode,
      }),
    );
    const center = calculatePolygonCentroid(resolved.boundaryRings[0] ?? []);
    if (center) dispatch(setMapCenter(center));
  };
  const [hasReviewedDraft, setHasReviewedDraft] = useState(false);
  const [easAudio, setEasAudio] = useState({ file: null, duration: 0 });
  const easAudioFile = ipawsDraft.deliveryChannels?.includes('EAS') ? easAudio.file : null;

  // The area every map shape must stay inside: the authorized area the draft targets — not the
  // workplace's own licensed zones (Mike, 2026-08-28: an operator may alert any area the COG
  // holds; outside the targeted one the server records and rejects). "Not ready" is never
  // permission to draw anywhere; its reason is shown as a finding.
  const permittedArea = useMemo(
    () =>
      permittedAreaForDraft(ipawsDraft, authorizedJurisdictions, {
        isLoading: femaJurisdictions.isLoading,
        isError: femaJurisdictions.isError,
      }),
    [authorizedJurisdictions, femaJurisdictions.isError, femaJurisdictions.isLoading, ipawsDraft],
  );

  // Development tools only (Mike, 2026-09-17: "those will never show on production"). Three
  // gates, all required: the build gate above (any other build has no such code), the environment
  // gate from the server's meta (a local session never offers them against a live COG), and an
  // authorized area to target. The environment gate reads the CHOSEN class when the meta serves
  // `environments[]` (multi-COG contract §2), else the class that the service serves.
  const femaMeta = useGetFemaMetaQuery();
  const chosenFemaEnvironment = getChosenFemaEnvironment(femaMeta.data, femaEnvironment);
  const IpawsDevTools = getIpawsDevTools();
  const showDevTools =
    IpawsDevTools !== null && chosenFemaEnvironment === 'TDL' && authorizedJurisdictions.length > 0;

  const updateDraft = (updates) => {
    dispatch(updateActiveAlertDraft(updates));
  };

  // Status and Scope by FEMA class (Mike, 2026-09-26): TDL and DEMO offer them all; PRODUCTION
  // sends Actual and Public only, so the other options are greyed out and the draft moves there.
  // The class comes from the one reader, `resolveFemaEnvironmentClass` (femaEnvironment.utils).
  const femaClass = resolveFemaEnvironmentClass(femaEnvironment, femaMeta.data);
  const authoringContractOptions = useMemo(
    () => ({
      ...contractOptions,
      status: greyOutIpawsOptions(contractOptions?.status ?? statusOptions, (value) =>
        isIpawsStatusAllowedForClass(value, femaClass),
      ),
      scope: greyOutIpawsOptions(contractOptions?.scope ?? scopeOptions, (value) =>
        isIpawsScopeAllowedForClass(value, femaClass),
      ),
    }),
    [contractOptions, femaClass],
  );
  const needsProductionStatusAndScope =
    femaClass === 'production' &&
    (ipawsDraft.status !== PRODUCTION_STATUS || ipawsDraft.scope !== PRODUCTION_SCOPE);

  useEffect(() => {
    if (!needsProductionStatusAndScope) return;
    dispatch(
      updateActiveAlertDraft({
        status: PRODUCTION_STATUS,
        scope: PRODUCTION_SCOPE,
        restriction: '',
        addresses: '',
      }),
    );
  }, [dispatch, needsProductionStatusAndScope]);

  const handleTextChange = ({ target: { name, value } }) => {
    updateDraft({ [name]: value });
  };

  const handleDateTimeChange = ({ target: { name, value } }) => {
    updateDraft({
      [name]: value ? formatIpawsDateTimeWithOffset(value) : '',
      ...(name === 'effective' || name === 'expires' ? { hasConfirmedExpires: false } : {}),
    });
  };

  const draftFindings = useMemo(() => {
    const list = getIpawsDraftFindings(ipawsDraft, {
      permittedArea,
      controlledOptions: contractOptions,
      channelRules,
      authorizedJurisdictions,
    });
    // A SAME code the COG does not hold is refused by FEMA (305/219): a finding, not a surprise.
    const unauthorized = unauthorizedGeocodeFinding(ipawsDraft, authorizedJurisdictions);
    if (unauthorized) list.push({ fieldPath: 'geocodes', message: unauthorized });
    // Card FE-01 and multi-COG contract §2: a selected FEMA environment that cannot send blocks
    // Preview.
    const environmentFinding = getFemaEnvironmentFinding(ipawsDraft.femaEnvironment, femaMeta.data);
    if (environmentFinding) list.push(environmentFinding);
    return list;
  }, [
    authorizedJurisdictions,
    channelRules,
    contractOptions,
    femaMeta.data,
    ipawsDraft,
    permittedArea,
  ]);

  // Run checks as the draft changes, but wait for Review or Preview before showing every message.
  const findings = hasReviewedDraft ? draftFindings : [];

  // Give the submission builder one object containing the form text and every map shape.
  const ipawsAlertData = useMemo(() => {
    const destination = describeFemaDestination(femaMeta, femaEnvironment);
    return {
      headline: ipawsDraft.headline,
      description: ipawsDraft.description,
      expirationTime: ipawsDraft.expires,
      issuedTime: ipawsDraft.effective,
      onsetTime: ipawsDraft.onset,
      messageType: ipawsDraft.messageType,
      references: ipawsDraft.references,
      status: ipawsDraft.status,
      scope: ipawsDraft.scope,
      restriction: ipawsDraft.restriction,
      addresses: ipawsDraft.addresses,
      areaShapes: ipawsDraft.areaShapes,
      ipaws: {
        // The preview reads it. The CAP payload builder does not.
        environment: chosenFemaEnvironment,
        event: ipawsDraft.event,
        eventCode: ipawsDraft.eventCode,
        deliveryChannels: ipawsDraft.deliveryChannels,
        easOrgCode: channelRules.easOrgCode,
        category: ipawsDraft.category,
        responseType: ipawsDraft.responseType,
        urgency: ipawsDraft.urgency,
        severity: ipawsDraft.severity,
        certainty: ipawsDraft.certainty,
        instruction: ipawsDraft.instruction,
        web: ipawsDraft.web,
        contact: ipawsDraft.contact,
        areaDescription: ipawsDraft.areaDesc,
        geocodes: ipawsDraft.geocodes,
        language: ipawsDraft.language,
        parameters: ipawsDraft.parameters,
        languages: ipawsDraft.languages,
        resources: easAudioFile
          ? [
              {
                resourceDesc: IPAWS_EAS_AUDIO_RESOURCE_DESCRIPTION,
                mimeType: IPAWS_EAS_AUDIO_RESOURCE_MIME_TYPE,
                size: easAudioFile.size,
              },
            ]
          : [],
      },
      destinationDetails: {
        destinationName: destination.destinationName,
        environmentLabel: destination.environment.replace(/^Environment:\s*/, ''),
        cogDisplayId: destination.cogDisplayId,
        issuingAuthority: destination.issuingAuthority,
        cogId: destination.cogId,
        cogName: destination.cogName,
        credentialStatus: destination.credentialStatus,
        dissemination: destination.dissemination,
      },
    };
  }, [
    channelRules.easOrgCode,
    chosenFemaEnvironment,
    easAudioFile,
    femaEnvironment,
    femaMeta,
    ipawsDraft,
  ]);

  const updateTarget = femaUpdateContext
    ? { id: femaUpdateContext.id, identifier: femaUpdateContext.payload.identifier }
    : null;

  const {
    previewModel,
    isPreviewOpen,
    isSubmissionResultOpen,
    isSubmitting,
    statusMessage,
    submissionResultMessage,
    startPreview,
    confirmAndSubmit,
    closePreview,
    closeSubmissionResult,
  } = useIpawsSubmissionFlow({
    alertData: ipawsAlertData,
    audioFile: easAudioFile,
    // Pass map and boundary errors forward so Preview and Send use the same checks as the form.
    clientValidationIssues: draftFindings.map(({ message }) => message),
    updateTarget,
    // Multi-COG contract §1: a new send carries the chosen class beside `data`. An older backend
    // serves no `environments[]`, and then the send carries no class. An Update never carries it.
    environment: getWireFemaEnvironment(femaMeta.data, femaEnvironment),
    onAcceptedSubmit: () => {
      dispatch(updateActiveAlertDraft(createInitialIpawsAlertDraft()));
      dispatch(clearFemaUpdateContext());
      setEasAudio({ file: null, duration: 0 });
    },
  });

  // Leaves Update mode without sending anything: back to a fresh IPAWS draft.
  const handleStopUpdating = () => {
    dispatch(clearFemaUpdateContext());
    dispatch(updateActiveAlertDraft(createInitialIpawsAlertDraft()));
  };

  // Do not open Preview until the operator can see and fix every current form or map problem.
  const handlePreview = () => {
    if (draftFindings.length) {
      setHasReviewedDraft(true);
      return;
    }
    startPreview();
  };

  return (
    <>
      {femaUpdateContext ? (
        <Alert
          variant="info"
          className="d-flex justify-content-between align-items-center gap-2 flex-wrap"
        >
          <span>
            Updating {updateTarget.identifier}. The Update supersedes the newest live version of
            this alert. Earlier versions stay active at FEMA until the alert is cancelled.
          </span>
          <Button type="button" variant="outline-secondary" size="sm" onClick={handleStopUpdating}>
            Stop updating
          </Button>
        </Alert>
      ) : null}

      <IpawsAuthoringSections
        draft={ipawsDraft}
        findings={findings}
        contractOptions={authoringContractOptions}
        environment={chosenFemaEnvironment}
        authorizedWeaHandlingOptions={femaJurisdictions.data?.weaHandlingOptions}
        isWeaHandlingLoading={femaJurisdictions.isLoading || femaMeta.isLoading}
        isWeaHandlingError={femaJurisdictions.isError || femaMeta.isError}
        channelRules={channelRules}
        onDraftChange={updateDraft}
        onTextChange={handleTextChange}
        onDateTimeChange={handleDateTimeChange}
        permittedArea={permittedArea}
        hasReviewedDraft={hasReviewedDraft}
        onReviewDraft={() => setHasReviewedDraft(true)}
        onClearJurisdiction={() => dispatch(clearIpawsResolvedJurisdiction())}
        authorizedAreas={authorizedAreas}
        onSelectAuthorizedJurisdiction={selectAuthorizedJurisdiction}
        audio={easAudio}
        onAudioChange={setEasAudio}
      />

      <div className="alert-creator-actions d-flex flex-column gap-2">
        {showDevTools ? (
          <IpawsDevTools
            jurisdiction={authorizedJurisdictions[0]}
            easOrgCode={channelRules.easOrgCode}
            disabled={isSubmitting}
            onFilled={() => setHasReviewedDraft(true)}
          />
        ) : null}
        <Button
          type="button"
          variant="success"
          className="alert-creator-action alert-creator-submit w-100"
          onClick={handlePreview}
          disabled={isSubmitting}
        >
          Preview
        </Button>
      </div>

      {statusMessage.text && (
        <Alert variant={statusMessage.variant} className="alert-creator-status-message">
          {statusMessage.text}
          {statusMessage.findings?.length ? (
            <ul className="mb-0 mt-2 ps-3">
              {statusMessage.findings.map((finding) => (
                <li key={`${finding.field}-${finding.message}`}>
                  {finding.field}: {finding.message}
                </li>
              ))}
            </ul>
          ) : null}
        </Alert>
      )}

      <IpawsSubmissionPreviewModal
        show={isPreviewOpen}
        onClose={closePreview}
        onConfirm={confirmAndSubmit}
        isSubmitting={isSubmitting}
        previewModel={previewModel}
      />
      <IpawsSubmissionResultModal
        show={isSubmissionResultOpen}
        onClose={closeSubmissionResult}
        resultMessage={submissionResultMessage}
        destinationDetails={previewModel}
      />
    </>
  );
};

export default IpawsAlertFields;
